/**
 * source.appconfig.* API (credentials-model §6bis) — the ADMIN deployment
 * config: Cat-M overrides (google/telegram client_id etc.) and Cat-S keys
 * (anysite) stored under the reserved `__deployment__` subject. The list RPC is
 * admin-gated (403 for non-admins) — the deployment section hides itself then.
 * Statuses only; the backend never returns values.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useRpcMutation, useRpcQuery } from "../../../runtime/hooks/useRpc";

export interface SourceAppConfigKey {
  readonly key: string;
  readonly label: string;
  /** Whether an override is set under `__deployment__`. */
  readonly deployment_configured: boolean;
}

export interface SourceAppConfigEntry {
  readonly source_id: string;
  readonly display_name: string;
  /** R2.1 category tag: shared_provider (Cat-S → the Sources panel) vs
   * module (Cat-M module config → the Deployment section). */
  readonly category: "shared_provider" | "module";
  readonly keys: readonly SourceAppConfigKey[];
}

export interface SourceAppConfigList {
  readonly vault_available: boolean;
  readonly sources: readonly SourceAppConfigEntry[];
}

const LIST_KEY = ["settings", "source-appconfig"] as const;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useSourceAppConfig() {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: LIST_KEY });
  };

  // Admin-gated: a non-admin gets a 403 error, which the section reads as
  // "not an admin → hide".
  const list = useRpcQuery<SourceAppConfigList>(LIST_KEY, "source.appconfig.list", undefined, {
    retry: false,
  });
  const setOverride = useRpcMutation<
    { ok: boolean },
    { source_id: string; key: string; value: string }
  >("source.appconfig.set", { onSuccess: invalidate });

  return { list, setOverride };
}
